<template>
  <columns>

    <template v-if="step === 1">
      <column class="is-3">
        <h3 class="is-size-4">Create App</h3>
        <p>Provide the basic information required to get started.</p>
      </column>

      <column>
        <section class="box">

          <text-input
            v-focus
            classes="is-medium takes-focus"
            @input="updateName"
            :value="app.name"
            :error="$root.errorBag.name"
            :placeholder="`ex, ${user.name_components.possessive_first} Blog`">
            Give your app a name
          </text-input>

          <text-input
            classes="is-medium"
            @input="updateDomain"
            :value="app.domain"
            :error="$root.errorBag.domain"
            :placeholder="`Enter the full production URL, such as ${exampleUrl}`">
            Where does your app live?
          </text-input>

          <action-button
            class="button is-success"
            @click="store"
            v-bind="{ working }">
            Create App
          </action-button>

        </section>
      </column>
    </template>

    <template v-if="step === 2">
      <column class="is-3">
        <h3 class="is-size-4">App Setup</h3>
        <p>Next, you’ll need to install the Snitch SDK and initiate a scan.</p>
      </column>

      <column>
        <section class="box">
          <content-wrapper>
            <p>Your app has been created, and is now waiting for the first scan to begin.</p>
            <p>To finish setting up your app, complete the steps below:</p>
            <p class="has-text-weight-bold">Install the Snitch SDK:</p>
            <div class="code">$ composer require snitchy/laravel-sdk</div>
            <p class="has-text-weight-bold">Add this to your environment configuration (.env or secrets manager):</p>
            <env-keys v-bind="{ keys }" />
            <p class="has-text-weight-bold">Start the first scan</p>
            <div class="code">$ php artisan snitch:scan</div>
          </content-wrapper>
        </section>

        <grid :gap="1" class="box">
          <pipeline-event
            v-for="event in pipeline.events"
            :key="event.name"
            v-bind="{ event }"
          />
        </grid>

        <div class="buttons" v-if="pipeline.isComplete">
          <action-button right-icon="arrow-right" class="is-primary has-text-weight-bold">Proceed to Dashboard</action-button>
        </div>
      </column>
    </template>

  </columns>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { kebabCase } from 'lodash'
import { PipelineEvent } from './onboarding/pipeline-event'
import { Pipeline } from './onboarding/pipeline'
import { default as PipelineEventComponent } from './onboarding/PipelineEvent'

export default {

  components: {
    PipelineEvent: PipelineEventComponent
  },

  data: () => ({
    step: 2,
    environmentId: null,
    working: false,

    pipeline: new Pipeline([
      new PipelineEvent(
        'EnvironmentPingReceived',
        'Awaiting initial connection…',
        'Initial connection made',
      ),
      new PipelineEvent(
        'EnvironmentAttributesUpdated',
        'Process environment attributes…',
        'Environment attributes received and processed',
      ),
      new PipelineEvent(
        'EnvironmentVulnerabilityVerificationsStored',
        'Process vulnerability verfifications…',
        'Vulnerability verifications received and processed',
      ),
    ])
  }),

  mounted() {
    this.$bus.$emit('set-title', 'Let’s get started with your first app')

    this.environmentId = 4 // Testing
    this.subscribeToEnvironmentChannel()
  },

  destroyed() {
    this.unsubscribeFromEnvironmentChannel()
  },

  methods: {
    ...mapMutations('app', [
      'updateDomain',
      'updateName',
    ]),

    ...mapActions('app', [
      'storeApp'
    ]),

    store() {
      this.working = true

      this.storeApp().then((data) => {
        this.environmentId = data.environment_id
        this.subscribeToEnvironmentChannel()
        this.step++
      }).catch(error => {
        this.$toast.error(
          error.response?.status === 422
            ? 'Please check your input.'
            : error.response?.data?.message || 'Something went wrong'
        )
        this.working = false
      })

    },

    processEvent(event) {
      console.log('Event:', event)

      if (!Object.keys(this.pipeline).includes(event.name)) {
        return
      }

      this.pipeline.markEventAsComplete()
    },

    subscribeToEnvironmentChannel() {
      const channel = Echo.private(this.broadcastChannel)

      for (const [event, data] of Object.entries(this.pipeline)) {
        console.log('Registering listener for event:', event, data)
        channel.listen(event, this.processEvent)
      }
    },

    unsubscribeFromEnvironmentChannel() {
      Echo.private(this.broadcastChannel).leaveChannel()
    }
  },

  computed: {
    ...mapGetters('auth', [
      'user'
    ]),

    ...mapGetters('app', [
      'app',
      'keys',
    ]),

    broadcastChannel() {
      return `environments.${this.environmentId}`
    },

    exampleUrl() {
      return `https://${kebabCase(this.user.name_components.possessive_first)}-blog.com`
    },
  }

}
</script>
